import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@softline/ui-core';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

export interface WorkflowCheckpointListItem {
  id: number;
  position: number;
  title: string;
  description?: string | null;
  status: string;
  lastStatusChange?: string | null;
}

export interface WorkflowCheckpointEdit<
  T extends WorkflowCheckpointListItem = WorkflowCheckpointListItem,
> {
  item: T;
  title: string;
  description: string;
}

export interface WorkflowCheckpointReorder<
  T extends WorkflowCheckpointListItem = WorkflowCheckpointListItem,
> {
  item: T;
  position: number;
}

interface Draft {
  title: string;
  description: string;
}

@Component({
  selector: 'soft-workflow-checkpoint-list',
  imports: [DatePipe, CdkDropList, CdkDrag, CdkDragHandle, IconComponent],
  templateUrl: './workflow-checkpoint-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowCheckpointListComponent<
  T extends WorkflowCheckpointListItem = WorkflowCheckpointListItem,
> {
  readonly checkpoints = input.required<readonly T[]>();
  readonly readonly = input(false, { transform: booleanAttribute });

  readonly toggle = output<T>();
  readonly edit = output<WorkflowCheckpointEdit<T>>();
  readonly remove = output<T>();
  readonly add = output<void>();
  readonly reorder = output<WorkflowCheckpointReorder<T>>();

  protected readonly items = linkedSignal(() => [...this.checkpoints()]);
  protected readonly drafts = signal<Record<number, Draft>>({});

  private readonly edits = new Subject<WorkflowCheckpointEdit<T>>();

  constructor() {
    this.edits
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (prev, curr) =>
            prev.item.id === curr.item.id &&
            prev.title === curr.title &&
            prev.description === curr.description
        ),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe((edit) => this.edit.emit(edit));

    effect(() => {
      const items = this.items();
      untracked(() =>
        this.drafts.update((drafts) => {
          const remaining = Object.fromEntries(
            Object.entries(drafts).filter(([id, draft]) => {
              const item = items.find((entry) => entry.id === Number(id));
              return (
                !!item &&
                (item.title !== draft.title ||
                  (item.description ?? '') !== draft.description)
              );
            })
          );
          return Object.keys(remaining).length === Object.keys(drafts).length
            ? drafts
            : remaining;
        })
      );
    });
  }

  protected isDone(item: T): boolean {
    return item.status !== 'O';
  }

  protected titleOf(item: T): string {
    return this.drafts()[item.id]?.title ?? item.title;
  }

  protected descriptionOf(item: T): string {
    return this.drafts()[item.id]?.description ?? item.description ?? '';
  }

  protected onInput(item: T, title: string, description: string) {
    this.drafts.update((drafts) => ({ ...drafts, [item.id]: { title, description } }));
    this.edits.next({ item, title, description });
  }

  protected onDrop(event: CdkDragDrop<T[]>) {
    if (event.previousIndex === event.currentIndex) return;
    const moved = this.items()[event.previousIndex];
    if (!moved) return;
    this.items.update((items) => {
      const next = [...items];
      moveItemInArray(next, event.previousIndex, event.currentIndex);
      return next;
    });
    this.reorder.emit({ item: moved, position: event.currentIndex + 1 });
  }
}
